'use client'

import { openLogin } from '@/components/auth/open-auth'
import { toast } from '@/components/common/toast'
import { CopyButton } from '@/components/extension/copy-button'
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalDescription,
  ResponsiveModalTitle,
  ResponsiveModalTrigger,
} from '@/components/extension/responsive-modal'
import { Button } from '@/components/ui/button'
import { useReferFriendQuery } from '@/hooks/react-query/account/use-reffer-friend.query'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import Link from 'next/link'
import { usePathname, useSearchParams } from 'next/navigation'

export const ModalDescriptionReferFriend = ({
  isOpen,
  onClose,
  isProductPage,
  productName,
  cb,
}: {
  isOpen: boolean
  onClose: () => void
  isProductPage?: boolean
  productName?: string
  cb?: () => void
}) => {
  const { data: referFriend, isLoading, refetch } = useReferFriendQuery()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const t = useTranslations('refer_friend_modal')

  const handleShareLink = async () => {
    if (!referFriend?.referral_code) {
      openLogin({ callback: () => refetch() })
      return
    }

    const url = getReferralUrl()
    const text = productName
      ? t('share_text', { name: productName })
      : t('share_text_generic')

    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({ title: text, url })
      } catch (err) {
        if (!(err instanceof Error && err.name === 'AbortError')) {
          navigator.clipboard.writeText(url)
          toast.message({
            title: t.rich('title', {
              br: (chunks) => <br />,
            }),
            message: t('copy_link'),
          })
        }
      }
    } else {
      navigator.clipboard.writeText(url)
      toast.message({
        title: t.rich('title', {
          br: (chunks) => <br />,
        }),
        message: t('copy_link'),
      })
    }
  }

  const getReferralUrl = () => {
    if (!referFriend?.referral_code) return ''

    if (isProductPage) {
      // Create a new URLSearchParams instance to preserve existing params
      const params = new URLSearchParams(searchParams.toString())
      // Add or update the refer-friend parameter
      params.set('refer-friend', referFriend.referral_code)

      return `${window.location.origin}${pathname}?${params.toString()}`
    } else {
      return `${process.env.NEXT_PUBLIC_SITE_URL}/?refer-friend=${referFriend.referral_code}`
    }
  }

  return (
    <ResponsiveModal
      open={isOpen}
      onOpenChange={onClose}
      aria-describedby={'modal-refer-friend'}
    >
      <ResponsiveModalTrigger className='hidden' />

      <ResponsiveModalContent
        className='gap-0 border-none bg-light px-6 py-9'
        aria-describedby={'modal-refer-friend'}
      >
        <ResponsiveModalTitle className='mb-4 text-center font-criteria text-lg font-medium leading-7 text-neutral-900 md:text-2xl lg:text-[28px] lg:leading-8'>
          {t.rich('title', {
            br: (chunks) => <br />,
          })}
        </ResponsiveModalTitle>
        <ResponsiveModalDescription />
        <div className='max-h-[70vh] overflow-auto'>
          {isLoading ? (
            <div className='mb-6 rounded-3xl bg-neutral-100 p-6 px-5'>
              <div className='mb-2 h-6 w-48 animate-pulse rounded-md bg-neutral-200'></div>

              <div className='flex items-center justify-between rounded-full bg-light px-4 py-3'>
                <div className='h-4 w-32 animate-pulse rounded-md bg-neutral-200'></div>
                <div className='h-8 w-20 animate-pulse rounded-md bg-neutral-200'></div>
              </div>

              <div className='mb-2 mt-6 h-6 w-48 animate-pulse rounded-md bg-neutral-200'></div>

              <div className='flex items-center justify-between rounded-full bg-light px-4 py-3'>
                <div className='h-4 w-32 animate-pulse rounded-md bg-neutral-200'></div>
                <div className='h-8 w-20 animate-pulse rounded-md bg-neutral-200'></div>
              </div>

              <div className='mt-6 border-t border-neutral-300 pt-2'>
                <div className='h-12 w-full animate-pulse rounded-md bg-neutral-200'></div>
              </div>
            </div>
          ) : !referFriend || !referFriend?.referral_code ? (
            <></>
          ) : (
            <div className='mb-6 rounded-3xl bg-neutral-100 p-6 px-5'>
              <h2 className='mb-2 font-bold'>{t('your_code')}</h2>

              <div className='flex items-center justify-between rounded-full bg-light px-4 py-3'>
                <p className='font-sans text-sm leading-4.5 text-neutral-600'>
                  {referFriend?.referral_code}
                </p>

                <CopyButton
                  value={referFriend?.referral_code || ''}
                  className='uppercase'
                />
              </div>
              <h2 className='mb-2 mt-6 font-bold'>{t('your_link')}</h2>
              <div className='flex items-center justify-between rounded-full bg-light px-4 py-3'>
                <p className='font-sans text-sm leading-4.5 text-neutral-600'>
                  Coolmate.me/{referFriend?.referral_code}
                </p>

                <CopyButton value={getReferralUrl()} className='uppercase' />
              </div>
            </div>
          )}

          <div className='rounded-3xl bg-neutral-100 p-6 pt-8 text-center'>
            <ul className='grid grid-cols-1 gap-6.5'>
              <ul className='no-scrollbar mt-3 flex gap-3 overflow-auto'>
                {list.map((item, idx) => (
                  <li
                    key={item.title}
                    className='relative flex min-w-[120px] flex-col items-center text-center'
                  >
                    <Image
                      src={ImageHelper.getImageUrl(item.imageUrl, '375_425')}
                      alt='referral'
                      width={44}
                      height={44}
                      unoptimized
                      className='rounded-2xl'
                    />
                    <p className='mt-2 whitespace-nowrap font-sans text-sm font-bold leading-4.5 text-primary'>
                      {t(`step${idx + 1}_title`)}
                    </p>
                    <p className='font-sans text-xs leading-3.5 text-neutral-500'>
                      {t(`step${idx + 1}_desc`)}
                    </p>
                    {idx < 2 && (
                      <Image
                        src={'/icons/icon-curved-arrow-dashed.svg'}
                        alt='arrow curved dashed'
                        width={72}
                        height={16}
                        className={`absolute -right-9 top-2 ${idx === 1 ? 'top-4 scale-y-[-1]' : ''}`}
                      />
                    )}
                  </li>
                ))}
              </ul>
            </ul>
            <div className='mt-6 flex flex-col gap-3'>
              <Button variant={'black'} onClick={handleShareLink}>
                <Image
                  alt='icon share'
                  src='/icons/icon-share-white.svg'
                  width={19}
                  height={21}
                />
                {referFriend?.referral_code
                  ? t('share_with_friend')
                  : t('login_to_share')}
              </Button>

              <Link
                href='/page/chuong-trinh-gioi-thieu-ban-moi'
                onClick={() => cb && cb()}
                className='block text-center text-sm text-neutral-500 underline'
              >
                {t('see_policy')}
              </Link>
            </div>
          </div>
        </div>
      </ResponsiveModalContent>
    </ResponsiveModal>
  )
}

const list = [
  {
    imageUrl: '/uploads/February2025/mceclip2_16.png',
    title: '', // dùng t('step1_title')
    description: '', // dùng t('step1_desc')
  },
  {
    imageUrl: '/uploads/February2025/mceclip0_87.png',
    title: '', // dùng t('step2_title')
    description: '', // dùng t('step2_desc')
  },
  {
    imageUrl: '/uploads/February2025/mceclip1_85.png',
    title: '', // dùng t('step3_title')
    description: '', // dùng t('step3_desc')
  },
]
